import { Button } from "../ui/Button";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { useT } from "../../lib/i18n";
import { validate } from "../../lib/validate";
import { vnum } from "../../lib/server-config";
import type { Level } from "../../lib/model";
import type { ValidateResult } from "../../lib/validate";
import type { Snapshot } from "../../lib/version";
import { DiffView } from "./DiffView";
import { RefreshModePicker } from "./RefreshModePicker";
import { useEffect, useState } from "react";
import { fetchAudit } from "../../lib/api";
import { contentLogEntries, groupSessions } from "../../lib/version-log";
import { useChangeLabels } from "./changeLabels";
import type { RefreshMode } from "../../lib/refresh-mode";
import type { Version } from "../../lib/version";

/** 发布确认里那份「改了什么」：线上生效版本的快照要现拉，拉的时候 / 拉挂了各显示一句 */
export type PublishDiff = { state: "loading" } | { state: "error" } | { state: "ready"; left: Snapshot; right: Snapshot };

const list = (t: ReturnType<typeof useT>, bad: Level[]) =>
  bad.slice(0, 6).map((l) => t("ver.levelShort", { id: l.id })).join("、") + (bad.length > 6 ? ` ${t("ver.levelEtc")}` : "");

/** 发布确认：有未通过的关时不给发布按钮，只给「查看未通过」 */
export function PublishDialog({
  levels, results, curVid, from, base, onlineId, diff, isSuper, onClose, onPublish, onCheck, draftNote = "",
}: {
  /** 草稿已有的备注；空的话按这一版的修改记录预填一句摘要 */
  draftNote?: string;
  /** 草稿的底子（拷自的已发布版本）；存量的草稿分叉为空 */
  base?: string | null;
  /** 能不能选「强制刷新」（服务端同样把关） */
  isSuper: boolean;
  /** 和线上生效版本比的改动；没有生效版本（首发）时不给 */
  diff?: PublishDiff;
  levels: Level[];
  results: ValidateResult[];
  curVid: string;
  from: string | null;
  /** null = 还没有生效版本，本次发布即首个生效版本 */
  onlineId: string | null;
  onClose: () => void;
  /** note：要随发布写进版本的备注；和草稿原有的一样时为 undefined（不动） */
  onPublish: (mode: RefreshMode, note?: string) => void;
  onCheck: () => void;
}) {
  const t = useT();
  const { where, field } = useChangeLabels();
  const [mode, setMode] = useState<RefreshMode>("normal");
  /*
   * 备注（设计稿 app.jsx PublishDialog）：草稿没写过就按修改记录预填「谁 改了几处：前几处的位置与字段」，可改。
   * 人一动手就不再覆盖；取不到修改记录就留空，不挡发布
   */
  const [note, setNote] = useState(draftNote);
  const [touched, setTouched] = useState(false);
  const [auto, setAuto] = useState(false);
  useEffect(() => {
    if (draftNote) return;
    let alive = true;
    fetchAudit({ version: vnum(curVid), pageSize: 100 }).then((d) => {
      if (!alive || touched) return;
      const sessions = groupSessions(contentLogEntries(d.items));
      const rows = sessions.flatMap((s) => s.rows);
      if (!rows.length) return;
      const who = [...new Set(sessions.map((s) => s.actor).filter(Boolean))].join("、");
      const items = [...new Set(rows.map((c) => `${where(c)} ${field(c)}`))];
      setNote(t("vlog.autoNote", { who, n: rows.length, items: items.slice(0, 3).join("、"), more: items.length > 3 ? t("vlog.autoNoteMore") : "" }).slice(0, 255));
      setAuto(true);
    }).catch(() => {});
    return () => { alive = false; };
    // 只在打开时取一次；touched 变了也不重取
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curVid, draftNote]);
  const noteOut = note.trim() === draftNote.trim() ? undefined : note.trim();
  const bad = levels.filter((_, i) => !results[i].ok);
  /*
   * 底子不是线上那一版：服务端发布时会先把线上相对底子的改动合进草稿（admin/config-merge.ts），
   * 两边改了同一处就拒绝发布、列出是哪几处。下面那份对比是眼前草稿和线上比，还没算这次合并，
   * 线上新加的改动在对比里会显示成「被改回去」——这里要先说清，免得人以为要退回它们。
   * 存量草稿没有底子（从草稿分叉的）时按旧规则：来源比线上旧就警告会覆盖
   */
  const stale = base && onlineId ? base !== onlineId : !!(from && onlineId && vnum(from) < vnum(onlineId));
  return (
    <Modal
      title={t("ver.pubTitle")}
      width={diff ? 900 : 560}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          {bad.length
            ? <Button tone="primary" onClick={onCheck}>{t("ver.pubViewBad", { n: bad.length })}</Button>
            : <Button tone="danger" onClick={() => onPublish(mode, noteOut)}>{t("ver.pubConfirm")}</Button>}
        </>
      }
    >
      {stale && !bad.length && (
        base
          ? <Notice tone="warning">{t("ver.pubMerge", { base, online: String(onlineId) })}</Notice>
          : <Notice tone="danger">{t("ver.pubStale", { base: String(from), online: String(onlineId) })}</Notice>
      )}
      {bad.length ? (
        <Notice tone="danger">{t("ver.pubBlocked", { n: bad.length, list: list(t, bad) })}</Notice>
      ) : (
        <Notice tone="warning">
          {onlineId
            ? t("ver.pubWarn", { id: curVid + (from ? `（${t("ver.rollbackFrom", { id: from })}）` : ""), online: onlineId })
            : t("ver.pubWarnFirst", { id: curVid })}
        </Notice>
      )}
      <div className="mt-3 text-sm text-ink-secondary">{t("ver.pubCount", { ok: levels.length - bad.length, total: levels.length })}</div>
      {!bad.length && (
        <label className="mt-4 block">
          <span className="mb-1.5 block text-sm font-semibold text-ink">
            {t("ver.pubNote")}{auto && !touched && <span className="ml-2 font-normal text-ink-placeholder">{t("ver.pubNoteAuto")}</span>}
          </span>
          <textarea value={note} maxLength={255} onChange={(e) => { setNote(e.target.value); setTouched(true); }}
            className="h-16 w-full resize-y rounded-base border border-line p-2 text-sm outline-none focus:border-primary" />
        </label>
      )}
      {!bad.length && <div className="mt-4"><RefreshModePicker value={mode} onChange={setMode} isSuper={isSuper} /></div>}
      {/* 发布前最后看一眼这次到底带上去什么：和线上生效版本比，每个模块一个页签 */}
      {diff && onlineId && (
        <div className="mt-4">
          <div className="mb-2 text-sm font-semibold text-ink">{t("ver.pubDiffTitle", { online: onlineId })}</div>
          {diff.state === "loading" && <div className="text-sm text-ink-placeholder">{t("ver.working")}</div>}
          {diff.state === "error" && <Notice tone="danger">{t("ver.pubDiffFail", { online: onlineId })}</Notice>}
          {diff.state === "ready" && (
            <div className="max-h-[55vh] overflow-y-auto">
              <DiffView tabbed left={diff.left} right={diff.right} leftLabel={t("ver.online", { id: onlineId })} rightLabel={curVid} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/** 回滚落地结果：新草稿已建好，立即全量校验并给下一步 */
export function RollbackResult({
  id, src, levels, onClose, onFix, onPublish,
}: { id: string; src: string; levels: Level[]; onClose: () => void; onFix: (id: number) => void; onPublish: () => void }) {
  const t = useT();
  const bad = levels.map((l) => ({ l, r: validate(l) })).filter((x) => !x.r.ok);
  return (
    <Modal
      title={t("ver.rbResultTitle", { id, src })}
      width={560}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("ver.rbKeep")}</Button>
          {bad.length
            ? <Button tone="primary" onClick={() => onFix(bad[0].l.id)}>{t("ver.rbFix", { id: bad[0].l.id })}</Button>
            : <Button tone="danger" onClick={onPublish}>{t("ver.rbPub")}</Button>}
        </>
      }
    >
      <Notice tone={bad.length ? "danger" : "info"}>
        {t("ver.rbCheck", { ok: levels.length - bad.length, total: levels.length })}
        {bad.length ? t("ver.rbBad", { list: list(t, bad.map((x) => x.l)) }) : t("ver.rbOk")}
      </Notice>
      <div className="mt-3 text-sm text-ink-secondary">{t("ver.rbUntouched", { src })}</div>
    </Modal>
  );
}

/**
 * 设为生效（设计稿 app.jsx 的 ActivateDialog）：已发布、没在生效的版本原样换上线，内容不变。
 * 入口：版本历史每个已发布版本的「设为生效」，以及只读横幅上的「激活这一版」。
 */
export function ActivateDialog({ v, onlineId, isSuper, busy, initialMode = "normal", onClose, onActivate }: {
  v: Version;
  /** 默认选哪一档：这一版刚发布 / 激活失败过，就沿用当时选的，别让人重选一遍 */
  initialMode?: RefreshMode;
  /** null = 还没有生效版本 */
  onlineId: string | null;
  isSuper: boolean;
  busy: boolean;
  onClose: () => void;
  onActivate: (mode: RefreshMode) => void;
}) {
  const t = useT();
  // 非超管拿到的默认值是 force（超管发布后激活失败、换普通账号来重试）时退回正常，服务端也不会放行
  const [mode, setMode] = useState<RefreshMode>(initialMode === "force" && !isSuper ? "normal" : initialMode);
  const older = onlineId != null && vnum(v.id) < vnum(onlineId);
  /*
   * 号比线上新、底子却不是线上：发布之后线上又换过一版，那一版的改动不在这里面，设为生效等于把它们退回去。
   * 号更旧的是有意回滚，上面 older 那句已经说了
   */
  const behind = !older && onlineId != null && !!v.base && v.base !== onlineId && v.id !== onlineId;
  const id = v.note ? `${v.id}（${v.note}）` : v.id;
  return (
    <Modal
      title={t("ver.actTitle", { id: v.id })}
      width={560}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button tone="danger" disabled={busy} onClick={() => onActivate(mode)}>{busy ? t("ver.working") : t("ver.actConfirm")}</Button>
        </>
      }
    >
      <Notice tone="warning">{onlineId ? t("ver.actWarn", { id, online: onlineId }) : t("ver.actWarnFirst", { id })}</Notice>
      {older && <div className="mt-3"><Notice tone="info">{t("ver.actOlder", { id: v.id, online: String(onlineId) })}</Notice></div>}
      {behind && <div className="mt-3"><Notice tone="danger">{t("ver.actBehind", { id: v.id, base: String(v.base), online: String(onlineId) })}</Notice></div>}
      <div className="mt-4"><RefreshModePicker value={mode} onChange={setMode} isSuper={isSuper} /></div>
    </Modal>
  );
}
